import {
  DemandLineItemTypes,
  LineItemType,
} from 'common/src/types/enums/LineItemType.enum';

const commonItemTypes = [
  LineItemType.Charges,
  LineItemType.Other,
  LineItemType.Taxes,
  LineItemType.Usage,
];

export enum DemandUnit {
  lbh = 'lb/h',
  kW = 'kW',
  MW = 'MW',
}

export enum Service {
  AverageInfluentFlow = 'Average Influent Flow',
  Biodiesel = 'Biodiesel',
  ChilledWater = 'Chilled Water',
  Coal = 'Coal',
  CompressedNaturalGas = 'Compressed Natural Gas',
  Diesel = 'Diesel',
  Electric = 'Electric',
  FuelOil = 'Fuel Oil',
  Gasoline = 'Gasoline',
  Hydrant = 'Hydrant',
  Irrigation = 'Irrigation',
  NaturalGas = 'Natural Gas',
  OnSiteSolar = 'On-Site Solar',
  OnSiteWind = 'On-Site Wind',
  Propane = 'Propane',
  Recycling = 'Recycling',
  Refuse = 'Refuse',
  RenewableDiesel = 'Renewable Diesel',
  Steam = 'Steam',
  Stormwater = 'Stormwater',
  Telecom = 'Telecom',
  Wastewater = 'Wastewater',
  Water = 'Water',
  Wood = 'Wood',
}

export const ServiceTypesForBulkDelivery = [
  Service.Coal,
  Service.Diesel,
  Service.FuelOil,
  Service.Propane,
  Service.Wood,
];

export enum UsageUnit {
  AcreFeet = 'Acre-Feet',
  cf = 'cf',
  ccf = 'ccf',
  Container = 'Container',
  CuYds = 'CuYds',
  Decatherms = 'Decatherms',
  EDU = 'EDU',
  Gallons = 'Gallons',
  KBtu = 'KBtu',
  Kgal = 'Kgal',
  kLbs = 'kLbs',
  kWh = 'kWh',
  lbs = 'lbs',
  Mcf = 'Mcf',
  MGD = 'Million Gal/Day',
  Minutes = 'Minutes',
  Mlbs = 'Mlbs',
  MMBtu = 'MMBtu',
  MWh = 'MWh',
  Therms = 'Therms',
  TonHr = 'Ton Hr',
  Tons = 'Tons',
}

export const ServiceDemandUnits: Partial<Record<Service, DemandUnit[]>> = {
  [Service.Electric]: [DemandUnit.kW, DemandUnit.MW],
  [Service.Steam]: [DemandUnit.lbh],
};

export const ServiceLineItemTypes: Record<Service, LineItemType[]> = {
  [Service.AverageInfluentFlow]: [LineItemType.Charges, LineItemType.Usage],
  [Service.Biodiesel]: commonItemTypes,
  [Service.ChilledWater]: commonItemTypes,
  [Service.Coal]: commonItemTypes,
  [Service.CompressedNaturalGas]: commonItemTypes,
  [Service.Diesel]: commonItemTypes,
  [Service.Electric]: [
    ...commonItemTypes,
    ...DemandLineItemTypes,
    LineItemType.OnsiteGeneration,
    LineItemType.SolarGridPurchased,
    LineItemType.SolarOnsiteExcess,
  ],
  [Service.FuelOil]: commonItemTypes,
  [Service.Gasoline]: commonItemTypes,
  [Service.Hydrant]: commonItemTypes,
  [Service.Irrigation]: commonItemTypes,
  [Service.NaturalGas]: commonItemTypes,
  [Service.OnSiteSolar]: commonItemTypes,
  [Service.OnSiteWind]: commonItemTypes,
  [Service.Propane]: commonItemTypes,
  [Service.Recycling]: commonItemTypes,
  [Service.Refuse]: commonItemTypes,
  [Service.RenewableDiesel]: commonItemTypes,
  [Service.Steam]: [...commonItemTypes, ...DemandLineItemTypes],
  [Service.Stormwater]: commonItemTypes,
  [Service.Telecom]: commonItemTypes,
  [Service.Wastewater]: commonItemTypes,
  [Service.Water]: commonItemTypes,
  [Service.Wood]: commonItemTypes,
};

// Standard usage units for Services
export const ServiceStandardUsageUnit: Record<Service, UsageUnit> = {
  [Service.AverageInfluentFlow]: UsageUnit.MGD,
  [Service.Biodiesel]: UsageUnit.Gallons,
  [Service.ChilledWater]: UsageUnit.TonHr,
  [Service.Coal]: UsageUnit.Tons,
  [Service.CompressedNaturalGas]: UsageUnit.Mcf,
  [Service.Diesel]: UsageUnit.Gallons,
  [Service.Electric]: UsageUnit.kWh,
  [Service.FuelOil]: UsageUnit.Gallons,
  [Service.Gasoline]: UsageUnit.Gallons,
  [Service.Hydrant]: UsageUnit.Kgal,
  [Service.Irrigation]: UsageUnit.Gallons,
  [Service.NaturalGas]: UsageUnit.ccf,
  [Service.OnSiteSolar]: UsageUnit.kWh,
  [Service.OnSiteWind]: UsageUnit.kWh,
  [Service.Propane]: UsageUnit.Gallons,
  [Service.Recycling]: UsageUnit.lbs,
  [Service.Refuse]: UsageUnit.Tons,
  [Service.RenewableDiesel]: UsageUnit.Gallons,
  [Service.Steam]: UsageUnit.TonHr,
  [Service.Stormwater]: UsageUnit.Gallons,
  [Service.Telecom]: UsageUnit.Minutes,
  [Service.Wastewater]: UsageUnit.Gallons,
  [Service.Water]: UsageUnit.Gallons,
  [Service.Wood]: UsageUnit.lbs,
};

// Possible usage units for Services
export const ServiceUsageUnits: Record<Service, string[]> = {
  [Service.AverageInfluentFlow]: [UsageUnit.MGD],
  [Service.Biodiesel]: [UsageUnit.Gallons],
  [Service.ChilledWater]: [UsageUnit.TonHr],
  [Service.Coal]: [UsageUnit.kLbs, UsageUnit.lbs, UsageUnit.Tons],
  [Service.CompressedNaturalGas]: [
    UsageUnit.ccf,
    UsageUnit.Decatherms,
    UsageUnit.KBtu,
    UsageUnit.Mcf,
    UsageUnit.MMBtu,
    UsageUnit.Therms,
  ],
  [Service.Diesel]: [UsageUnit.Gallons],
  [Service.Electric]: [UsageUnit.kWh, UsageUnit.MWh],
  [Service.FuelOil]: [UsageUnit.Gallons, UsageUnit.Tons],
  [Service.Gasoline]: [UsageUnit.Gallons],
  [Service.Hydrant]: [
    UsageUnit.cf,
    UsageUnit.ccf,
    UsageUnit.EDU,
    UsageUnit.Gallons,
    UsageUnit.Kgal,
    UsageUnit.Mcf,
    UsageUnit.MGD,
  ],
  [Service.Irrigation]: [
    UsageUnit.AcreFeet,
    UsageUnit.cf,
    UsageUnit.ccf,
    UsageUnit.Gallons,
    UsageUnit.Kgal,
    UsageUnit.Mcf,
  ],
  [Service.NaturalGas]: [
    UsageUnit.ccf,
    UsageUnit.Decatherms,
    UsageUnit.KBtu,
    UsageUnit.Mcf,
    UsageUnit.MMBtu,
    UsageUnit.Therms,
  ],
  [Service.OnSiteSolar]: [UsageUnit.kWh, UsageUnit.MWh],
  [Service.OnSiteWind]: [UsageUnit.kWh, UsageUnit.MWh],
  [Service.Propane]: [
    UsageUnit.cf,
    UsageUnit.ccf,
    UsageUnit.Gallons,
    UsageUnit.KBtu,
    UsageUnit.Kgal,
    UsageUnit.lbs,
    UsageUnit.Mcf,
    UsageUnit.MMBtu,
    UsageUnit.Therms,
  ],
  [Service.Refuse]: [
    UsageUnit.Container,
    UsageUnit.CuYds,
    UsageUnit.lbs,
    UsageUnit.Tons,
  ],
  [Service.Recycling]: [
    UsageUnit.Container,
    UsageUnit.CuYds,
    UsageUnit.lbs,
    UsageUnit.Tons,
  ],
  [Service.RenewableDiesel]: [UsageUnit.Gallons],
  [Service.Steam]: [
    UsageUnit.kLbs,
    UsageUnit.lbs,
    UsageUnit.Mlbs,
    UsageUnit.TonHr,
  ],
  [Service.Stormwater]: [
    UsageUnit.cf,
    UsageUnit.ccf,
    UsageUnit.EDU,
    UsageUnit.Gallons,
    UsageUnit.Kgal,
    UsageUnit.Mcf,
    UsageUnit.MGD,
  ],
  [Service.Telecom]: [UsageUnit.Minutes],
  [Service.Wastewater]: [
    UsageUnit.AcreFeet,
    UsageUnit.cf,
    UsageUnit.ccf,
    UsageUnit.EDU,
    UsageUnit.Gallons,
    UsageUnit.Kgal,
    UsageUnit.Mcf,
    UsageUnit.MGD,
  ],
  [Service.Water]: [
    UsageUnit.AcreFeet,
    UsageUnit.cf,
    UsageUnit.ccf,
    UsageUnit.Gallons,
    UsageUnit.Kgal,
    UsageUnit.Mcf,
  ],
  [Service.Wood]: [UsageUnit.lbs, UsageUnit.Tons],
};
